// src/utils/layout/specialDocBuilders.js
//
// Ozel dokuman tiplerine ait THREE.js grup olusturma:
// Kolon Sema (multi-meter), Pano Detay (multi-meter), Enerji Odasi, Baslik template.

import * as THREE from 'three';
import { dxfDrawCore } from '../../../modules/dxf';
import { generateKolonSemaMultiMeter } from '../../../utils/kolonSema/generateKolonSemaMultiMeter';
import { generatePanoDetayV2 } from '../../../utils/generatePanoDetayV2';
import { drawClippedRoom, drawPanelRect, drawPanelDimensions, drawCustomDimensions, drawDocumentFrame } from '../../../utils/energyRoomDrawing';
import { panelIntersectsWalls } from '../../../shared/snap/wallSnapUtils';
import { createDocumentHeaderParts } from '../../../utils/simpleTransformFunctions';
import { buildBaslikTemplateGroup } from '../../../utils/renderCustomBaslik';
import { transformSimpleSupplyToKolonConfig } from '../../../utils/simpleTransformFunctions';
import { PROJECT_TYPES } from '../../../constants/projectTypes';
import { HEADER_HEIGHT } from './layoutConstants';

export function buildKolonSemaMultiMeter(docDef, documentGroup, simpleSupply, floorPlans, panelTypes) {
  const { kolonConfig, tumKatlar } = transformSimpleSupplyToKolonConfig(simpleSupply, floorPlans, panelTypes);
  const hasPanels = kolonConfig.katlar?.some(k => k.paneller?.length > 0) ||
    simpleSupply?.components?.some(c => c.kind === 'meter_panel');
  if (!hasPanels) return;
  const tempGroup = new THREE.Group();
  generateKolonSemaMultiMeter(tempGroup, kolonConfig, tumKatlar, { centerX: 0, centerY: 0 });
  if (tempGroup.children.length === 0) return;
  tempGroup.updateMatrixWorld(true);
  const bbox = new THREE.Box3().setFromObject(tempGroup);
  const size = bbox.getSize(new THREE.Vector3());
  const center = bbox.getCenter(new THREE.Vector3());
  docDef.w = Math.ceil(size.x);
  docDef.h = Math.ceil(size.y);
  tempGroup.position.sub(center);
  tempGroup.position.add(new THREE.Vector3(docDef.w / 2, -docDef.h / 2, 0));
  documentGroup.add(tempGroup);
}

export function buildPanoDetayMultiMeter(docDef, documentGroup, panoDetayConfig) {
  const tempScene = new THREE.Group();
  generatePanoDetayV2(tempScene, panoDetayConfig, { centerX: 0, centerY: 0 });
  if (tempScene.children.length === 0) return;
  tempScene.updateMatrixWorld(true);
  const bbox = new THREE.Box3().setFromObject(tempScene);
  const size = bbox.getSize(new THREE.Vector3());
  const center = bbox.getCenter(new THREE.Vector3());
  docDef.w = Math.ceil(size.x);
  docDef.h = Math.ceil(size.y);
  tempScene.position.sub(center);
  tempScene.position.add(new THREE.Vector3(docDef.w / 2, -docDef.h / 2, 0));
  documentGroup.add(tempScene);
}

export function buildEnerjiOdasi(docDef, documentGroup, erData) {
  const { erClippedSegments, erPanels, erDrawingScale, erDimSettings, erCustomDimSettings, erCustomDimensions, erFrameMargin } = erData;
  if (!erClippedSegments || erClippedSegments.length === 0) return;
  const erScene = new THREE.Group();
  drawClippedRoom(erScene, erClippedSegments);
  erPanels.forEach(panel => {
    const intersects = panelIntersectsWalls(panel, erClippedSegments, erDrawingScale);
    drawPanelRect(erScene, panel, false, erDrawingScale, intersects);
  });
  if (erPanels.length > 0) drawPanelDimensions(erScene, erPanels, erDrawingScale, erDimSettings);
  if (erCustomDimensions.length > 0) drawCustomDimensions(erScene, erCustomDimensions, erDrawingScale, erCustomDimSettings);
  if (erFrameMargin) drawDocumentFrame(erScene, erScene, erFrameMargin, erDrawingScale);
  if (erScene.children.length === 0) return;
  erScene.updateMatrixWorld(true);
  const bbox = new THREE.Box3().setFromObject(erScene);
  const size = bbox.getSize(new THREE.Vector3());
  const center = bbox.getCenter(new THREE.Vector3());
  const erW = Math.ceil(size.x), erH = Math.ceil(size.y);
  const totalH = erH + HEADER_HEIGHT;
  docDef.w = erW; docDef.h = totalH;
  const erHeaderParts = createDocumentHeaderParts("ENERJ\u0130 ODASI", erW, totalH, HEADER_HEIGHT, { drawFrame: true });
  const erHeaderGroup = new THREE.Group();
  dxfDrawCore(erHeaderGroup, erHeaderParts, [], "scene");
  if (erHeaderGroup.children.length > 0) {
    const hBbox = new THREE.Box3().setFromObject(erHeaderGroup);
    erHeaderGroup.position.set(0, -hBbox.max.y, 0);
    documentGroup.add(erHeaderGroup);
  }
  erScene.position.sub(center);
  erScene.position.add(new THREE.Vector3(erW / 2, -HEADER_HEIGHT - erH / 2, 0));
  documentGroup.add(erScene);
}

export function buildBaslikFromTemplate(docDef, documentGroup, appStore) {
  const tplState = appStore.getState().baslikTemplate;
  const tplId = tplState?.activeTemplateId;
  const tpl = tplId ? tplState.templates.find(t => t.id === tplId) : null;
  if (!tpl?.dxfRawText) return;
  const customGroup = buildBaslikTemplateGroup(tpl, appStore.getState());
  if (!customGroup) return;
  const bbox = customGroup.userData?.bbox;
  docDef.w = bbox?.width ? Math.ceil(bbox.width) : docDef.w;
  docDef.h = bbox?.height ? Math.ceil(bbox.height) : docDef.h;
  documentGroup.add(customGroup);
}
